import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { QuantityInput } from '@/components/ui/quantity-input';
import { useAutoSave } from '@/hooks/use-auto-save';
import { CurrencyCode } from '@/lib/consts';
import { MeasurementSystem } from '@/types';

import {
  useInsertIngredientPrice,
  useInsertIngredientQuantities,
} from '../api/ingredients-mutations';
import { IngredientWithDetails } from '../types/ingredient-types';

// Create schema based on editability
const costFormSchema = z.object({
  price: z.coerce.number<number>().gt(0, { error: '*required' }),
  purchaseQty: z.coerce.number<number>().gt(0, { error: '*required' }),
  purchaseUnit: z.string().min(1, { error: '*required' }),
  massQty: z.coerce.number<number>().gt(0, { error: '*required' }),
  massUnit: z.string().min(1, { error: '*required' }),
  volumeQty: z.coerce.number<number>().gt(0, { error: '*required' }),
  volumeUnit: z.string().min(1, { error: '*required' }),
});

type CostForm = z.infer<typeof costFormSchema>;

interface IngredientCostFormPresentationProps {
  currencyCode: CurrencyCode;
  ingredient: IngredientWithDetails;
  onSubmit: (values: CostForm) => Promise<void>;
  isPriceEditable: boolean;
  isQuantityEditable: boolean;
  measurementSystem: MeasurementSystem;
}

function IngredientCostFormPresentation({
  ingredient,
  onSubmit,
  currencyCode,
  isPriceEditable,
  isQuantityEditable,
  measurementSystem,
}: IngredientCostFormPresentationProps) {
  const form = useForm<CostForm>({
    resolver: zodResolver(costFormSchema),
    defaultValues: {
      price: ingredient.price,
      purchaseQty: ingredient.purchase_qty,
      purchaseUnit: ingredient.purchase_unit,
      massQty: ingredient.each_mass_qty,
      massUnit: ingredient.each_mass_unit,
      volumeQty: ingredient.each_volume_qty,
      volumeUnit: ingredient.each_volume_unit,
    },
    shouldFocusError: false,
  });

  // Until the user changes the volume quantity, it should be the same as the mass quantity

  const watchedMassQty = form.watch('massQty');
  const previousMassQty = useRef<number>(watchedMassQty);

  useEffect(() => {
    if (form.getValues('volumeQty') == previousMassQty.current) {
      form.setValue('volumeQty', watchedMassQty);
    }
    previousMassQty.current = watchedMassQty;
  }, [watchedMassQty]);

  useAutoSave(form, onSubmit);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex justify-between items-center gap-2">
          <FormField
            key="price"
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem className="flex-1">
                <div className="flex items-center justify-between">
                  <FormLabel className="whitespace-nowrap text-md">
                    Purchase Price:
                  </FormLabel>
                  <FormMessage></FormMessage>
                </div>
                {/* TODO: Handle case where user switches org currency */}
                <CurrencyInput
                  {...field}
                  currencyCode={currencyCode}
                  onChange={field.onChange}
                  disabled={!isPriceEditable}
                />
              </FormItem>
            )}
          />
          <FormItem className="flex-1">
            <FormField
              key="purchaseQty"
              control={form.control}
              name="purchaseQty"
              render={() => (
                <div className="flex items-center justify-between">
                  <FormLabel className="whitespace-nowrap text-md">
                    Purchase Quantity:
                  </FormLabel>
                  <FormMessage></FormMessage>
                </div>
              )}
            />
            <FormControl>
              <QuantityInput
                control={form.control}
                quantityName="purchaseQty"
                unitName="purchaseUnit"
                unitType={['mass', 'volume', 'count']}
                disabled={!isQuantityEditable}
                measurementSystem={measurementSystem}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </div>
        <div>
          <div className="flex items-center gap-4">
            <FormItem className="flex-1">
              <FormLabel className="whitespace-nowrap text-md">Each</FormLabel>
              <FormControl>
                <div className="mt-2 h-[38px] flex items-center border rounded px-3">
                  1
                </div>
              </FormControl>
            </FormItem>

            <div className="flex self-end h-[38px]">=</div>

            <FormItem className="flex-1">
              <FormField
                key="massQty"
                control={form.control}
                name="massQty"
                render={() => (
                  <div className="flex items-center justify-between">
                    <FormLabel className="whitespace-nowrap text-md">
                      Unit Mass:
                    </FormLabel>
                    <FormMessage></FormMessage>
                  </div>
                )}
              />
              <FormControl>
                <QuantityInput
                  control={form.control}
                  quantityName="massQty"
                  unitName="massUnit"
                  unitType={['mass']}
                  disabled={!isQuantityEditable}
                  measurementSystem={measurementSystem}
                />
              </FormControl>
              <FormMessage />
            </FormItem>

            <div className="flex self-end h-[38px]">=</div>

            <FormItem className="flex-1">
              <FormField
                key="volumeQty"
                control={form.control}
                name="volumeQty"
                render={() => (
                  <div className="flex items-center justify-between">
                    <FormLabel className="whitespace-nowrap text-md">
                      Unit Volume:
                    </FormLabel>
                    <FormMessage></FormMessage>
                  </div>
                )}
              />
              <FormControl>
                <QuantityInput
                  control={form.control}
                  quantityName="volumeQty"
                  unitName="volumeUnit"
                  unitType={['volume']}
                  disabled={!isQuantityEditable}
                  measurementSystem={measurementSystem}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>
          <span className="text-slate-500 text-sm flex items-center gap-1 mt-2">
            <Info />
            Defining the per item weight and volume makes Prepsheets more
            accurate
          </span>
        </div>
      </form>
    </Form>
  );
}

interface IngredientCostFormProps {
  ingredient: IngredientWithDetails;
  organisationId: string | null;
  isPriceEditable: boolean;
  isQuantityEditable: boolean;
  currencyCode: CurrencyCode;
  measurementSystem: MeasurementSystem;
}

export function IngredientCostForm({
  ingredient,
  organisationId,
  isPriceEditable,
  isQuantityEditable,
  currencyCode,
  measurementSystem,
}: IngredientCostFormProps) {
  const insertIngredientPrice = useInsertIngredientPrice();
  const insertIngredientQuantities = useInsertIngredientQuantities();

  const onSubmit = useCallback(
    async (values: CostForm) => {
      const latestPrice = ingredient.price;

      // Check if the price has changed
      if (isPriceEditable && latestPrice != values.price) {
        await insertIngredientPrice.mutateAsync([
          {
            ingredient_id: ingredient.id,
            organisation_id: organisationId,
            price: values.price,
          },
        ]);
      }

      // Check if a quantity has changed
      if (
        isQuantityEditable &&
        (ingredient.purchase_qty != values.purchaseQty ||
          ingredient.purchase_unit != values.purchaseUnit ||
          ingredient.each_mass_qty != values.massQty ||
          ingredient.each_mass_unit != values.massUnit ||
          ingredient.each_volume_qty != values.volumeQty ||
          ingredient.each_volume_unit != values.volumeUnit)
      ) {
        await insertIngredientQuantities.mutateAsync([
          {
            ingredient_id: ingredient.id,
            organisation_id: organisationId,
            purchase_qty: values.purchaseQty,
            purchase_unit: values.purchaseUnit,
            each_mass_qty: values.massQty,
            each_mass_unit: values.massUnit,
            each_volume_qty: values.volumeQty,
            each_volume_unit: values.volumeUnit,
          },
        ]);
      }
    },
    [ingredient],
  );

  return (
    <IngredientCostFormPresentation
      ingredient={ingredient}
      onSubmit={onSubmit}
      key={ingredient.id}
      currencyCode={currencyCode}
      isPriceEditable={isPriceEditable}
      isQuantityEditable={isQuantityEditable}
      measurementSystem={measurementSystem}
    />
  );
}
